import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Badge,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Fade,
  Zoom
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  MoreVert as MoreIcon,
  ViewModule as GridIcon,
  ViewList as ListIcon,
  FilterList as FilterIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Folder as FolderIcon,
  Groups as GroupsIcon,
  Collections as CollectionsIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Image as ImageIcon,
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  DateRange as DateRangeIcon,
  ExpandMore as ExpandMoreIcon,
  Launch as LaunchIcon,
  ViewCompact as CompactIcon,
  ViewStream as ExpandedIcon,
  ViewWeek as TimelineIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  DragIndicator as DragIcon,
  RocketLaunch as LaunchCampaignIcon,
  Sell as SalesIcon,
  Event as EventIcon,
  Palette as PaletteIcon,
  FontDownload as FontIcon,
  Category as BrandIcon,
  Upload as UploadIcon,
  ColorLens as ColorIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Favorite as HeartIcon,
  ChatBubbleOutline as CommentIcon,
  Share as ShareIcon,
  LibraryAdd as AddSequenceIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../providers/AuthProvider';
import CampaignPostGrid from '../components/campaigns/CampaignPostGrid';
import CampaignPostList from '../components/campaigns/CampaignPostList';
import PostEditor from '../components/campaigns/PostEditor';
import ScheduleDialog from '../components/campaigns/ScheduleDialog';
import CampaignHeader from '../components/campaigns/CampaignHeader';
import { campaignApi, trackEvent } from '../services/campaignApi';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`campaign-tabpanel-${index}`}
    aria-labelledby={`campaign-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const ProgressCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.customShadows?.card || theme.shadows[1],
}));

const MetricCard = styled(Card)(({ theme }) => ({
  height: '100%',
  textAlign: 'center',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  }
}));

const ContentBucketCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  }
}));

const AssetCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  cursor: 'pointer',
  overflow: 'hidden',
  '&:hover': {
    '& .asset-overlay': {
      opacity: 1,
    }
  }
}));

// New styled components for improved UX
const CompactPostCard = styled(Paper)(({ theme, approved }) => ({
  padding: theme.spacing(1.5),
  border: '1px solid',
  borderColor: approved ? theme.palette.success.main : theme.palette.divider,
  backgroundColor: approved ? theme.palette.success.light + '10' : theme.palette.background.paper,
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: approved ? theme.palette.success.dark : theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2],
    '& .post-actions': {
      opacity: 1,
    },
    '& .post-content': {
      WebkitLineClamp: 'unset',
    }
  },
  '& .post-actions': {
    opacity: 0,
    transition: 'opacity 0.2s ease',
  }
}));

const SequenceAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: theme.spacing(2, 0),
    boxShadow: theme.shadows[3],
  },
  '& .MuiAccordionSummary-root': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
    minHeight: 72,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.action.hover,
    }
  }
}));

const PlatformIcon = ({ platform, size = 20 }) => {
  const iconProps = { sx: { fontSize: size } };
  const icons = {
    twitter: <TwitterIcon {...iconProps} sx={{ color: '#1DA1F2', fontSize: size }} />,
    linkedin: <LinkedInIcon {...iconProps} sx={{ color: '#0077B5', fontSize: size }} />,
    facebook: <FacebookIcon {...iconProps} sx={{ color: '#1877F2', fontSize: size }} />,
    instagram: <InstagramIcon {...iconProps} sx={{ color: '#E4405F', fontSize: size }} />
  };
  return icons[platform] || null;
};

const DateTimeChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  '& svg': {
    fontSize: 14,
  }
}));

const PostMetaRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  flexWrap: 'wrap',
}));

// Campaign goal configurations with expected outcomes
const getCampaignGoalConfig = () => ({
  brand_awareness: {
    icon: VisibilityIcon,
    label: 'Brand Awareness',
    color: 'primary',
    metrics: [
      { label: 'Expected Reach', value: '10K-50K', description: 'Potential audience reach across platforms' },
      { label: 'Engagement Rate', value: '3-5%', description: 'Average engagement on awareness posts' },
      { label: 'Brand Mentions', value: '+25%', description: 'Increase in brand mentions' },
      { label: 'Follower Growth', value: '+15%', description: 'Expected follower increase' }
    ]
  },
  product_launch: {
    icon: LaunchCampaignIcon,
    label: 'Product Launch',
    color: 'secondary',
    metrics: [
      { label: 'Launch Reach', value: '25K-100K', description: 'Product announcement visibility' },
      { label: 'Click-through Rate', value: '5-8%', description: 'Expected CTR to product page' },
      { label: 'Early Adopters', value: '100-500', description: 'Estimated early sign-ups' },
      { label: 'Buzz Generation', value: 'High', description: 'Social media buzz level' }
    ]
  },
  sales_promotion: {
    icon: SalesIcon,
    label: 'Sales Promotion',
    color: 'success',
    metrics: [
      { label: 'Conversion Rate', value: '2-4%', description: 'Expected sales conversion' },
      { label: 'Revenue Impact', value: '+20%', description: 'Projected revenue increase' },
      { label: 'Promo Reach', value: '15K-60K', description: 'Promotion visibility' },
      { label: 'ROI', value: '3-5x', description: 'Return on investment' }
    ]
  },
  event_promotion: {
    icon: EventIcon,
    label: 'Event Promotion',
    color: 'warning',
    metrics: [
      { label: 'Event Registrations', value: '50-200', description: 'Expected sign-ups' },
      { label: 'Event Awareness', value: '20K-80K', description: 'Event visibility reach' },
      { label: 'Social Shares', value: '500+', description: 'Expected social shares' },
      { label: 'Attendance Rate', value: '60-80%', description: 'Registration to attendance' }
    ]
  }
});

const CampaignReviewPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  
  // Get campaign goal config
  const CAMPAIGN_GOAL_CONFIG = getCampaignGoalConfig();
  
  // State
  const [campaign, setCampaign] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [editingPost, setEditingPost] = useState(null);
  const [creatingPost, setCreatingPost] = useState(null); // New state for creating posts
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  
  // New state for enhanced features
  const [contentBuckets, setContentBuckets] = useState([]);
  const [icpAnalysis, setIcpAnalysis] = useState(null);
  const [campaignAssets, setCampaignAssets] = useState([]);
  const [approvedPosts, setApprovedPosts] = useState(new Set());
  const [approvedBuckets, setApprovedBuckets] = useState(new Set());
  const [removedPosts, setRemovedPosts] = useState([]);
  const [recreateCampaignOpen, setRecreateCampaignOpen] = useState(false);
  const [postSchedules, setPostSchedules] = useState({}); // Track manual schedules per post
  const [sequenceViewMode, setSequenceViewMode] = useState('expanded'); // 'compact', 'expanded', 'timeline'
  const [expandedSequences, setExpandedSequences] = useState(new Set(['intro-brand'])); // Only first sequence expanded by default
  const [assetFilter, setAssetFilter] = useState('all'); // Asset filter state
  const [addSequenceDialogOpen, setAddSequenceDialogOpen] = useState(false); // Add sequence dialog state
  const [brandAssets, setBrandAssets] = useState({
    colors: [],
    fonts: [],
    icons: [],
    logos: []
  }); // Brand assets state

  // Demo campaign data (moved from CampaignDashboardPage)
  const DEMO_CAMPAIGN = {
    id: 'demo-campaign-2024',
    name: '🚀 TechFlow Pro Product Launch Campaign',
    websiteUrl: 'https://techflowpro.com',
    campaignGoal: 'product_launch',
    status: 'active',
    businessProfile: {
      businessName: 'TechFlow Pro',
      industry: 'Software Development',
      targetAudience: 'Small to medium businesses, entrepreneurs, tech professionals',
      businessDescription: 'TechFlow Pro is a comprehensive project management and workflow automation platform designed for modern teams. We help businesses streamline their processes, increase productivity, and scale efficiently.',
      location: 'San Francisco, CA'
    },
    totalPosts: 24,
    publishedPosts: 18,
    scheduledPosts: 6,
    draftPosts: 0,
    platforms: ['instagram', 'twitter', 'linkedin', 'facebook'],
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-20T14:30:00Z',
    startDate: '2024-01-15T00:00:00Z',
    endDate: '2024-02-15T23:59:59Z',
    description: 'Comprehensive product launch campaign for TechFlow Pro v2.0, featuring new AI-powered automation tools, enhanced collaboration features, and mobile app launch.',
    tags: ['product-launch', 'ai-tools', 'productivity', 'saas', 'mobile-app'],
    budget: 15000,
    isDemo: true
  };

  // Demo posts data
  const DEMO_POSTS = [
    {
      id: 'demo-post-1',
      campaignId: 'demo-campaign-2024',
      platform: 'twitter',
      content: '🚀 Excited to announce TechFlow Pro v2.0! Our new AI-powered automation tools will revolutionize how teams manage projects. Say goodbye to manual workflows and hello to efficiency! #TechFlowPro #ProductLaunch #AI #Productivity',
      postType: 'announcement',
      status: 'published',
      sequenceOrder: 1,
      mediaSuggestions: [
        {
          type: 'image',
          description: 'Product hero shot with AI automation interface',
          url: '/images/techflow-hero.jpg'
        }
      ],
      createdAt: '2024-01-15T09:00:00Z',
      updatedAt: '2024-01-15T09:00:00Z',
      scheduledFor: '2024-01-15T09:00:00Z',
      publishedAt: '2024-01-15T09:00:00Z'
    },
    {
      id: 'demo-post-2',
      campaignId: 'demo-campaign-2024',
      platform: 'linkedin',
      content: 'The future of project management is here! 🌟 TechFlow Pro v2.0 introduces intelligent automation that learns from your team\'s workflows and optimizes them in real-time. Perfect for growing businesses who want to scale efficiently without sacrificing quality.\n\n✨ Key Features:\n• AI-powered task automation\n• Smart resource allocation\n• Real-time collaboration tools\n• Advanced analytics dashboard\n\nReady to transform your project management? Learn more at our website.',
      postType: 'feature_highlight',
      status: 'published',
      sequenceOrder: 2,
      mediaSuggestions: [
        {
          type: 'carousel',
          description: 'Feature showcase carousel - automation, collaboration, analytics',
          url: '/images/techflow-features-carousel.jpg'
        }
      ],
      createdAt: '2024-01-15T14:00:00Z',
      updatedAt: '2024-01-15T14:00:00Z',
      scheduledFor: '2024-01-15T14:00:00Z',
      publishedAt: '2024-01-15T14:00:00Z'
    },
    {
      id: 'demo-post-3',
      campaignId: 'demo-campaign-2024',
      platform: 'instagram',
      content: '🎯 Behind the scenes: How TechFlow Pro saves teams 15+ hours per week\n\nOur AI automation handles the repetitive stuff so your team can focus on what matters most - creating amazing results! 💪\n\n#TechFlowPro #TeamProductivity #WorkSmarter #AI #Automation #ProjectManagement',
      postType: 'behind_the_scenes',
      status: 'published',
      sequenceOrder: 3,
      mediaSuggestions: [
        {
          type: 'video',
          description: 'Time-lapse video showing automated workflow in action',
          url: '/videos/techflow-automation-demo.mp4'
        }
      ],
      createdAt: '2024-01-16T10:30:00Z',
      updatedAt: '2024-01-16T10:30:00Z',
      scheduledFor: '2024-01-16T10:30:00Z',
      publishedAt: '2024-01-16T10:30:00Z'
    },
    {
      id: 'demo-post-4',
      campaignId: 'demo-campaign-2024',
      platform: 'facebook',
      content: '📊 Customer Success Story: How StartupCo increased their productivity by 300% using TechFlow Pro\n\n"Before TechFlow Pro, our team was drowning in manual processes. Now, everything runs smoothly and we can focus on growing our business!" - Sarah Johnson, CEO of StartupCo\n\n🎉 Ready to see similar results? Try TechFlow Pro v2.0 today and discover how AI can transform your project management.',
      postType: 'customer_success',
      status: 'scheduled',
      sequenceOrder: 4,
      mediaSuggestions: [
        {
          type: 'image',
          description: 'Customer testimonial graphic with stats and quote',
          url: '/images/techflow-testimonial.jpg'
        }
      ],
      createdAt: '2024-01-16T15:00:00Z',
      updatedAt: '2024-01-16T15:00:00Z',
      scheduledFor: '2024-01-22T11:00:00Z'
    },
    {
      id: 'demo-post-5',
      campaignId: 'demo-campaign-2024',
      platform: 'twitter',
      content: '🧵 Thread: The 5 biggest project management pain points we solved with TechFlow Pro v2.0\n\n1/6 🧵',
      postType: 'thread_start',
      status: 'scheduled',
      sequenceOrder: 5,
      mediaSuggestions: [],
      createdAt: '2024-01-17T08:00:00Z',
      updatedAt: '2024-01-17T08:00:00Z',
      scheduledFor: '2024-01-23T09:00:00Z'
    },
    {
      id: 'demo-post-6',
      campaignId: 'demo-campaign-2024',
      platform: 'linkedin',
      content: '🎯 Join us for the TechFlow Pro v2.0 Virtual Launch Event!\n\n📅 January 25th, 2024 at 2:00 PM PST\n🎪 Live product demos, Q&A with our engineering team, and exclusive early access offers\n🎁 First 100 attendees get a free premium trial extension\n\nRegister now - link in comments! Can\'t wait to show you what we\'ve been building. 🚀\n\n#TechFlowPro #VirtualEvent #ProductLaunch #LiveDemo',
      postType: 'event_promotion',
      status: 'scheduled',
      sequenceOrder: 6,
      mediaSuggestions: [
        {
          type: 'image',
          description: 'Virtual event announcement with date, time, and registration CTA',
          url: '/images/techflow-event.jpg'
        }
      ],
      createdAt: '2024-01-17T12:00:00Z',
      updatedAt: '2024-01-17T12:00:00Z',
      scheduledFor: '2024-01-24T10:00:00Z'
    }
  ];

  // Generate content buckets based on posts' sequence information
  const generateMockContentBuckets = (posts) => {
    const campaignStartDate = new Date();
    campaignStartDate.setDate(campaignStartDate.getDate() + 1); // Start tomorrow
    
    // Group posts by sequence name
    const sequenceGroups = {};
    posts.forEach(post => {
      const sequenceName = post.sequenceName || 'Uncategorized';
      if (!sequenceGroups[sequenceName]) {
        sequenceGroups[sequenceName] = [];
      }
      sequenceGroups[sequenceName].push(post);
    });
    
    // Define sequence metadata for each campaign type
    const sequenceMetadata = {
      // Product Launch sequences
      'Pre-Launch Teasers': {
        id: 'pre-launch',
        description: 'Build anticipation and mystery around your upcoming product',
        objective: 'Generate buzz and early interest',
        timing: 'Week 1',
        frequency: 'Daily leading up to launch',
        tags: ['teaser', 'anticipation', 'coming-soon']
      },
      'Launch Announcement': {
        id: 'launch-announcement',
        description: 'The big reveal - announce your product with maximum impact',
        objective: 'Create excitement and drive initial adoption',
        timing: 'Week 2',
        frequency: 'Multiple posts on launch day, then daily for 3 days',
        tags: ['launch', 'announcement', 'new-product']
      },
      'Feature Deep Dives': {
        id: 'feature-deep-dives',
        description: 'Showcase specific features and use cases in detail',
        objective: 'Educate users and demonstrate value',
        timing: 'Week 3',
        frequency: 'Every other day',
        tags: ['features', 'education', 'use-cases']
      },
      'Social Proof & Momentum': {
        id: 'social-proof-momentum',
        description: 'Share user testimonials, milestones, and media mentions',
        objective: 'Build credibility and FOMO',
        timing: 'Week 4',
        frequency: 'Every 2-3 days',
        tags: ['testimonials', 'milestones', 'press']
      },
      
      // Brand Awareness sequences
      'Brand Introduction': {
        id: 'brand-intro',
        description: 'Tell your brand story and introduce your team',
        objective: 'Build brand recognition and trust',
        timing: 'Week 1',
        frequency: 'Every 2-3 days',
        tags: ['brand-story', 'team', 'values']
      },
      'Industry Insights': {
        id: 'industry-insights',
        description: 'Share expertise through statistics, trends, and myth-busting',
        objective: 'Establish thought leadership',
        timing: 'Week 2',
        frequency: 'Every 2 days',
        tags: ['insights', 'trends', 'education']
      },
      'Customer Success Stories': {
        id: 'customer-success',
        description: 'Highlight transformations and real results',
        objective: 'Demonstrate proven value through customer wins',
        timing: 'Week 3',
        frequency: 'Every 3 days',
        tags: ['case-studies', 'success', 'results']
      },
      'Community & Engagement': {
        id: 'community-engagement',
        description: 'Foster community interaction and brand loyalty',
        objective: 'Build an engaged community around your brand',
        timing: 'Week 4',
        frequency: 'Every 2 days',
        tags: ['community', 'engagement', 'interaction']
      },
      
      // Sales Promotion sequences
      'Problem Agitation': {
        id: 'problem-agitation',
        description: 'Highlight the pain points and costs of the status quo',
        objective: 'Create urgency by quantifying the problem',
        timing: 'Week 1',
        frequency: 'Every 2 days',
        tags: ['problem', 'pain-points', 'costs']
      },
      'Solution Introduction': {
        id: 'solution-intro',
        description: 'Present your solution with clear benefits and ROI',
        objective: 'Show how you solve the problem better than alternatives',
        timing: 'Week 2',
        frequency: 'Daily for maximum impact',
        tags: ['solution', 'benefits', 'roi']
      },
      'Urgency & Scarcity': {
        id: 'urgency-scarcity',
        description: 'Create FOMO with limited-time offers and bonuses',
        objective: 'Drive immediate action through scarcity',
        timing: 'Week 3',
        frequency: 'Daily with increasing urgency',
        tags: ['limited-time', 'bonus', 'scarcity']
      },
      'Final Push & Guarantee': {
        id: 'final-push',
        description: 'Remove risk and paint vision of success',
        objective: 'Convert remaining prospects with guarantees',
        timing: 'Week 4',
        frequency: 'Multiple times on final days',
        tags: ['guarantee', 'last-chance', 'vision']
      },
      
      // Event Promotion sequences
      'Event Announcement': {
        id: 'event-announcement',
        description: 'Announce event details and build initial interest',
        objective: 'Generate awareness and early registrations',
        timing: 'Week 1',
        frequency: 'Every 2 days',
        tags: ['save-the-date', 'event', 'announcement']
      },
      'Speaker & Content Highlights': {
        id: 'speaker-highlights',
        description: 'Showcase speakers and valuable content',
        objective: 'Build credibility and show event value',
        timing: 'Week 2',
        frequency: 'Daily speaker reveals',
        tags: ['speakers', 'agenda', 'content']
      },
      'Social Proof & FOMO': {
        id: 'event-social-proof',
        description: 'Show who\'s attending and share testimonials',
        objective: 'Create FOMO and urgency to register',
        timing: 'Week 3',
        frequency: 'Every day as event approaches',
        tags: ['attendees', 'testimonials', 'fomo']
      },
      'Final Push': {
        id: 'event-final-push',
        description: 'Last chance messaging and event prep',
        objective: 'Maximize final registrations and prep attendees',
        timing: 'Week 4',
        frequency: 'Multiple times per day in final days',
        tags: ['last-chance', 'preparation', 'excitement']
      }
    };
    
    // Create buckets from grouped posts
    const buckets = [];
    let sequenceNumber = 1;
    let currentDate = campaignStartDate;
    
    Object.entries(sequenceGroups).forEach(([sequenceName, sequencePosts]) => {
      // Sort posts by sequence order
      sequencePosts.sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));
      
      const metadata = sequenceMetadata[sequenceName] || {
        id: sequenceName.toLowerCase().replace(/\s+/g, '-'),
        description: `Posts related to ${sequenceName}`,
        objective: 'Achieve campaign goals',
        timing: `Week ${sequenceNumber}`,
        frequency: 'As scheduled',
        tags: [sequenceName.toLowerCase()]
      };
      
      // Calculate dates based on frequency
      const frequencyDays = {
        'Daily': 1,
        'Every other day': 2,
        'Every 2 days': 2,
        'Every 2-3 days': 2.5,
        'Every 3 days': 3,
        'Every 4 days': 4,
        'As scheduled': 2
      };
      
      const daysBetweenPosts = frequencyDays[metadata.frequency] || 2;
      
      // Assign scheduled dates to posts
      const scheduledPosts = sequencePosts.map((post, idx) => {
        const scheduledDate = new Date(currentDate.getTime() + (idx * daysBetweenPosts * 24 * 60 * 60 * 1000));
        const hour = 9 + (sequenceNumber - 1) * 3; // Stagger posting times by sequence
        
        return {
          ...post,
          bucketId: metadata.id,
          scheduledDate,
          scheduledTime: `${hour.toString().padStart(2, '0')}:00`
        };
      });
      
      const startDate = new Date(currentDate);
      const endDate = new Date(currentDate.getTime() + (scheduledPosts.length * daysBetweenPosts * 24 * 60 * 60 * 1000));
      
      buckets.push({
        id: metadata.id,
        name: sequenceName,
        description: metadata.description,
        objective: metadata.objective,
        postCount: scheduledPosts.length,
        posts: scheduledPosts,
        sequence: sequenceNumber,
        timing: metadata.timing,
        startDate,
        endDate,
        frequency: metadata.frequency,
        approved: false,
        tags: metadata.tags
      });
      
      // Move current date forward for next sequence
      currentDate = new Date(endDate.getTime() + (2 * 24 * 60 * 60 * 1000)); // 2 day gap between sequences
      sequenceNumber++;
    });
    
    return buckets;
  };

  // Mock ICP analysis data
  const mockIcpAnalysis = {
    demographics: {
      ageRange: '25-45',
      primaryGender: 'Mixed',
      location: 'Urban areas, primarily US',
      income: '$50K-$150K'
    },
    psychographics: {
      interests: ['Technology', 'Business Growth', 'Marketing', 'Social Media'],
      painPoints: ['Time management', 'Content creation', 'Consistent posting'],
      values: ['Efficiency', 'Professional growth', 'Work-life balance']
    },
    behaviors: {
      socialPlatforms: ['LinkedIn', 'Twitter', 'Instagram'],
      contentPreferences: ['How-to guides', 'Industry insights', 'Success stories'],
      engagementTimes: ['Morning (7-9 AM)', 'Lunch (12-1 PM)', 'Evening (6-8 PM)']
    },
    recommendations: [
      'Focus on educational content that provides immediate value',
      'Use data and statistics to build credibility',
      'Share quick tips and time-saving strategies',
      'Leverage user-generated content and testimonials'
    ]
  };

  // Mock campaign assets
  const mockCampaignAssets = [
    {
      id: 1,
      type: 'image',
      name: 'Brand Logo Variations',
      source: 'uploaded',
      url: '/api/placeholder/300/300',
      tags: ['logo', 'brand', 'identity']
    },
    {
      id: 2,
      type: 'image',
      name: 'Product Screenshots',
      source: 'generated',
      url: '/api/placeholder/300/300',
      tags: ['product', 'features', 'UI']
    },
    {
      id: 3,
      type: 'image',
      name: 'Team Photos',
      source: 'uploaded',
      url: '/api/placeholder/300/300',
      tags: ['team', 'people', 'culture']
    },
    {
      id: 4,
      type: 'template',
      name: 'Quote Template',
      source: 'generated',
      url: '/api/placeholder/300/300',
      tags: ['template', 'quote', 'testimonial']
    }
  ];

  // Mock brand assets
  const mockBrandAssets = {
    colors: [
      { id: 'c1', name: 'Primary Blue', value: '#1976D2', type: 'primary' },
      { id: 'c2', name: 'Secondary Orange', value: '#FF6B35', type: 'secondary' },
      { id: 'c3', name: 'Success Green', value: '#4CAF50', type: 'success' },
      { id: 'c4', name: 'Dark Gray', value: '#212121', type: 'text' },
      { id: 'c5', name: 'Light Gray', value: '#F5F5F5', type: 'background' }
    ],
    fonts: [
      { id: 'f1', name: 'Roboto', family: 'Roboto, sans-serif', type: 'primary' },
      { id: 'f2', name: 'Open Sans', family: 'Open Sans, sans-serif', type: 'secondary' },
      { id: 'f3', name: 'Playfair Display', family: 'Playfair Display, serif', type: 'heading' },
      { id: 'f4', name: 'Source Code Pro', family: 'Source Code Pro, monospace', type: 'code' }
    ],
    icons: [
      { id: 'i1', name: 'Rocket Launch', component: LaunchCampaignIcon, type: 'product' },
      { id: 'i2', name: 'Heart', component: HeartIcon, type: 'social' },
      { id: 'i3', name: 'Shield', component: CheckCircleIcon, type: 'security' },
      { id: 'i4', name: 'Growth', component: TrendingUpIcon, type: 'analytics' }
    ],
    logos: [
      { id: 'l1', name: 'Main Logo', url: '/api/placeholder/200/100', type: 'full' },
      { id: 'l2', name: 'Logo Mark', url: '/api/placeholder/100/100', type: 'mark' },
      { id: 'l3', name: 'White Logo', url: '/api/placeholder/200/100', type: 'white' }
    ]
  };

  useEffect(() => {
    loadCampaignData();
  }, [campaignId]);

  useEffect(() => {
    if (campaign) {
      trackEvent('campaign_review_viewed', {
        userId: user?.id,
        campaignId: campaign.id,
        totalPosts: posts.length
      });
    }
  }, [campaign, user]);

  const loadCampaignData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if this is the demo campaign
      if (campaignId === 'demo-campaign-2024') {
        // Load demo campaign data immediately
        setCampaign(DEMO_CAMPAIGN);
        setPosts(DEMO_POSTS);
        
        // Set mock data for new features
        const buckets = generateMockContentBuckets(DEMO_POSTS);
        setContentBuckets(buckets);
        setIcpAnalysis(mockIcpAnalysis);
        setCampaignAssets(mockCampaignAssets);
        setBrandAssets(mockBrandAssets);
        
        // Load demo approval status (some approved, some not for demo purposes)
        const approvedDemoPostIds = new Set(
          DEMO_POSTS
            .filter((post, index) => index < 2) // Approve first 2 posts for demo
            .map(post => post.id)
        );
        setApprovedPosts(approvedDemoPostIds);
        setLoading(false);
        return;
      }

      // For real campaigns, make API calls
      const [campaignResponse, postsResponse, sequencesResponse] = await Promise.all([
        campaignApi.getCampaign(campaignId),
        campaignApi.getCampaignPosts(campaignId),
        campaignApi.getContentSequences(campaignId)
      ]);

      if (campaignResponse.success && postsResponse.success) {
        setCampaign(campaignResponse.data);
        setPosts(postsResponse.data);
        
        // Use sequences from API if available, otherwise generate from posts
        if (sequencesResponse.success && sequencesResponse.data && sequencesResponse.data.length > 0) {
          setContentBuckets(sequencesResponse.data.map(seq => ({
            id: seq.id,
            name: seq.name,
            description: seq.description || '',
            objective: seq.objective || '',
            posts: seq.posts || [],
            postCount: seq.posts?.length || 0,
            approved: seq.approved || false,
            sequence: seq.sequence || 1,
            timing: seq.timing || 'immediate',
            tags: seq.tags || []
          })));
        } else {
          // Fallback to generating buckets from posts if no sequences from backend
          const buckets = generateMockContentBuckets(postsResponse.data);
          setContentBuckets(buckets);
        }
        
        // Load mock data for features not yet implemented
        setIcpAnalysis(mockIcpAnalysis);
        setCampaignAssets(mockCampaignAssets);
        setBrandAssets(mockBrandAssets);
        
        // Load actual approval status from backend (not auto-approve)
        const approvedPostIds = new Set(
          postsResponse.data
            .filter(post => post.approved === true)
            .map(post => post.id)
        );
        setApprovedPosts(approvedPostIds);
        
        // Update buckets with approval status based on posts
        setContentBuckets(prev => prev.map(bucket => {
          const bucketApprovedCount = bucket.posts.filter(p => 
            postsResponse.data.find(post => post.id === p.id)?.approved === true
          ).length;
          const allPostsApproved = bucket.posts.length > 0 && 
            bucketApprovedCount === bucket.posts.length;
          
          return { ...bucket, approved: allPostsApproved };
        }));
      } else {
        throw new Error('Failed to load campaign data');
      }
    } catch (error) {
      console.error('Failed to load campaign:', error);
      setError(error.message || 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handlePostEdit = (post) => {
    setEditingPost(post);
    trackEvent('campaign_post_edit_started', {
      userId: user?.id,
      campaignId,
      postId: post.id,
      platform: post.platform
    });
  };

  const handleCreatePost = (bucketId) => {
    // Create a new post template for the specified bucket
    const bucket = contentBuckets.find(b => b.id === bucketId);
    const newPost = {
      id: `new-post-${Date.now()}`,
      campaignId,
      bucketId,
      platform: 'twitter', // Default platform
      content: '',
      postType: 'announcement',
      status: 'draft',
      isNew: true,
      bucket: bucket
    };
    setCreatingPost(newPost);
    trackEvent('campaign_post_create_started', {
      userId: user?.id,
      campaignId,
      bucketId
    });
  };

  const handlePostSave = async (postId, updates) => {
    try {
      // Check if this is a new post being created
      if (creatingPost && creatingPost.isNew) {
        // Calculate sequence order based on existing posts
        const bucketPosts = contentBuckets.find(b => b.id === creatingPost.bucketId)?.posts || [];
        const maxSequenceOrder = Math.max(0, ...posts.map(p => p.sequenceOrder || p.sequence_order || 0));
        
        // Create new post via API
        const newPostData = {
          campaign: campaignId,
          sequence_id: creatingPost.bucketId, // Pass the sequence ID
          platform: updates.platform || 'twitter',
          content: updates.content || '',
          media_suggestions: updates.mediaUrls || updates.media_suggestions || [],
          status: 'draft',
          sequence_order: maxSequenceOrder + 1,
          // Add any additional fields from updates
          ...(updates.scheduled_for && { scheduled_for: updates.scheduled_for })
        };
        
        const response = await campaignApi.createCampaignPost(newPostData);
        
        if (response.success) {
          const newPost = response.data;
          
          // Add to posts list
          setPosts(prev => [...prev, newPost]);
          
          // Add to the appropriate bucket
          handleAddPostToSequence(newPost, creatingPost.bucketId);
          
          // If the post should be approved by default, handle approval
          if (updates.approved !== false) {
            await handlePostApproval(newPost.id, true);
          }
          
          setCreatingPost(null);
          showSnackbar('Post created and added to sequence', 'success');
          
          trackEvent('campaign_post_created', {
            userId: user?.id,
            campaignId,
            postId: newPost.id,
            bucketId: creatingPost.bucketId,
            platform: updates.platform
          });
        } else {
          throw new Error(response.error || 'Failed to create post');
        }
      } else {
        // Update existing post
        const response = await campaignApi.updateCampaignPost(postId, updates);
        
        if (response.success) {
          // Update posts list with response data
          setPosts(prev => prev.map(post => 
            post.id === postId ? { ...post, ...response.data } : post
          ));
          
          // Handle approval status change separately via API if it changed
          const currentPost = posts.find(p => p.id === postId);
          if (updates.approved !== undefined && currentPost?.approved !== updates.approved) {
            // Call the approval API separately to ensure proper backend handling
            await handlePostApproval(postId, updates.approved);
          } else if (updates.approved !== undefined) {
            // If approval didn't change, just update local state
            setApprovedPosts(prev => {
              const newSet = new Set(prev);
              if (updates.approved) {
                newSet.add(postId);
              } else {
                newSet.delete(postId);
              }
              return newSet;
            });
          }
          
          // Also update the post in content buckets
          setContentBuckets(prev => prev.map(bucket => ({
            ...bucket,
            posts: bucket.posts.map(post => 
              post.id === postId ? { ...post, ...response.data } : post
            )
          })));
          
          setEditingPost(null);
          setUnsavedChanges(false);
          showSnackbar('Post updated successfully', 'success');
          
          trackEvent('campaign_post_updated', {
            userId: user?.id,
            campaignId,
            postId,
            platform: updates.platform
          });
        } else {
          throw new Error('Failed to update post');
        }
      }
    } catch (error) {
      console.error('Failed to save post:', error);
      showSnackbar(error.message || 'Failed to save post', 'error');
    }
  };

  const handleBulkSchedule = () => {
    setScheduleDialogOpen(true);
  };

  const handleScheduleConfirm = async (scheduleData) => {
    try {
      // Check authentication first
      const hasToken = !!localStorage.getItem('token');
      const isDemoModeEnabled = JSON.parse(localStorage.getItem('solosocial_demo_mode') || 'false');
      
      if (!hasToken && !isDemoModeEnabled) {
        showSnackbar('Please log in to schedule posts', 'error');
        return;
      }

      // Get approved posts for scheduling
      const approvedPostsList = getApprovedPosts();
      
      if (approvedPostsList.length === 0) {
        showSnackbar('No approved posts to schedule', 'warning');
        return;
      }

      // Check if user has required social media accounts connected
      const uniquePlatforms = [...new Set(approvedPostsList.map(p => p.platform))];
      console.log('Posts require these platforms:', uniquePlatforms);

      setLoading(true);
      
      // Transform schedule data for backend API
      const startDateTime = new Date(scheduleData.startDate + 'T' + (scheduleData.customTime || '09:00')).toISOString();
      const postsPerDay = scheduleData.intervals ? Math.max(1, Math.min(10, Math.floor(24 / scheduleData.intervals))) : 2;
      
      const schedulePayload = {
        start_date: startDateTime,
        posts_per_day: postsPerDay,
        time_slots: scheduleData.customTime ? [scheduleData.customTime] : ['09:00', '15:00'],
        platforms: uniquePlatforms,
        skip_weekends: scheduleData.type === 'smart' // Smart scheduling might skip weekends
      };

      console.log('Scheduling posts with data:', schedulePayload);
      
      // Check if this is demo mode
      const isDemoMode = campaignId === 'demo-campaign-2024' || JSON.parse(localStorage.getItem('solosocial_demo_mode') || 'false');
      
      if (isDemoMode) {
        // Demo mode - simulate successful scheduling without API calls
        showSnackbar(`Demo: Successfully scheduled ${approvedPostsList.length} posts`, 'success');
        setScheduleDialogOpen(false);
        setSelectedPosts([]);
        
        trackEvent('campaign_posts_scheduled', {
          userId: user?.id,
          campaignId,
          postCount: approvedPostsList.length,
          scheduleType: scheduleData.type,
          demoMode: true
        });

        // Navigate to campaign dashboard after demo scheduling
        setTimeout(() => {
          navigate('/campaigns');
        }, 1500);
        return;
      }

      // Update campaign status to ready if needed
      if (campaign.status !== 'ready') {
        await campaignApi.updateCampaign(campaignId, { status: 'ready' });
      }

      // Call the campaign API to schedule posts
      const response = await campaignApi.scheduleCampaign(campaignId, schedulePayload);
      
      if (response.success) {
        // Update local campaign state
        setCampaign(prev => ({ 
          ...prev, 
          status: 'scheduled',
          scheduled_posts: response.scheduled_posts || approvedPostsList.length 
        }));
        
        showSnackbar(`Successfully scheduled ${approvedPostsList.length} posts`, 'success');
        setScheduleDialogOpen(false);
        setSelectedPosts([]);
        
        trackEvent('campaign_posts_scheduled', {
          userId: user?.id,
          campaignId,
          postCount: approvedPostsList.length,
          scheduleType: scheduleData.type
        });

        // Refresh campaign data to show scheduled posts
        await loadCampaignData();

        // Navigate to campaign dashboard after successful scheduling
        setTimeout(() => {
          navigate('/campaigns');
        }, 1500);
      } else {
        throw new Error(response.error || 'Failed to schedule posts');
      }
    } catch (error) {
      console.error('Failed to schedule posts:', error);
      
      // Handle specific authentication errors
      if (error.message.includes('Authentication') || error.message.includes('credentials')) {
        showSnackbar('Session expired. Please log in again to schedule posts.', 'error');
      } else if (error.message.includes('Unauthorized')) {
        showSnackbar('Authentication failed. Please log in again.', 'error');
      } else {
        showSnackbar(error.message || 'Failed to schedule posts', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredPosts = posts.filter(post => 
    filterPlatform === 'all' || post.platform === filterPlatform
  );

  const platforms = [...new Set(posts.map(post => post.platform))];

  // Bucket approval handlers
  const handleBucketApproval = async (bucketId, approved) => {
    // Find the bucket
    const bucket = contentBuckets.find(b => b.id === bucketId);
    if (!bucket) {
      showSnackbar('Bucket not found', 'error');
      return;
    }

    // Get all post IDs in the bucket
    const postIds = bucket.posts.map(post => post.id);
    
    if (postIds.length === 0) {
      showSnackbar('No posts in this sequence to approve', 'warning');
      return;
    }

    // Store previous state for rollback
    const previousBuckets = new Set(approvedBuckets);
    const previousPosts = new Set(approvedPosts);
    const previousPostsData = [...posts];
    const previousContentBuckets = [...contentBuckets];

    // ✅ OPTIMISTIC UPDATE - Update UI immediately
    setApprovedBuckets(prev => {
      const newSet = new Set(prev);
      if (approved) {
        newSet.add(bucketId);
      } else {
        newSet.delete(bucketId);
      }
      return newSet;
    });

    setContentBuckets(prev => prev.map(b => 
      b.id === bucketId ? { ...b, approved } : b
    ));

    // Update approved posts state optimistically
    if (approved) {
      // Approve all posts in the bucket
      setApprovedPosts(prev => {
        const newSet = new Set(prev);
        bucket.posts.forEach(post => newSet.add(post.id));
        return newSet;
      });
    } else {
      // Remove approval from all posts in the bucket
      setApprovedPosts(prev => {
        const newSet = new Set(prev);
        bucket.posts.forEach(post => newSet.delete(post.id));
        return newSet;
      });
    }

    // Update posts list with approval data optimistically
    setPosts(prev => prev.map(post => 
      postIds.includes(post.id) 
        ? { ...post, approved } 
        : post
    ));

    try {
      // Call backend API for bulk approval
      const response = await campaignApi.bulkApprovePosts(campaignId, postIds, approved);
      
      if (response.success) {
        // ✅ SUCCESS - Optimistic update was correct, show success message
        showSnackbar(
          `${approved ? 'Approved' : 'Unapproved'} ${postIds.length} posts in sequence`, 
          'success'
        );
        
        trackEvent('campaign_sequence_approval_changed', {
          userId: user?.id,
          campaignId,
          bucketId,
          approved,
          postsCount: postIds.length
        });
      } else {
        throw new Error(response.error || 'Failed to update sequence approval');
      }
    } catch (error) {
      console.error('Failed to update sequence approval:', error);
      
      // ❌ ROLLBACK - Restore previous state on API failure
      setApprovedBuckets(previousBuckets);
      setApprovedPosts(previousPosts);
      setPosts(previousPostsData);
      setContentBuckets(previousContentBuckets);
      
      showSnackbar(error.message || 'Failed to update sequence approval', 'error');
    }
  };

  // Post approval handlers
  const handlePostApproval = async (postId, approved) => {
    // Store previous state for rollback
    const previousApprovedPosts = new Set(approvedPosts);
    const previousPostsData = [...posts];
    
    // Find the current post
    const currentPost = posts.find(p => p.id === postId);
    if (!currentPost) {
      showSnackbar('Post not found', 'error');
      return;
    }

    // ✅ OPTIMISTIC UPDATE - Update UI immediately
    setApprovedPosts(prev => {
      const newSet = new Set(prev);
      if (approved) {
        newSet.add(postId);
      } else {
        newSet.delete(postId);
      }
      return newSet;
    });
    
    // Update posts list with approval data optimistically
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { 
            ...post, 
            approved: approved,
            approval_notes: approved ? 'Approved' : '',
            approved_at: approved ? new Date().toISOString() : null
          } 
        : post
    ));

    try {
      // Call backend API to update approval status
      const response = await campaignApi.togglePostApproval(postId, approved);
      
      if (response.success) {
        // ✅ SUCCESS - Update with actual backend data
        setApprovedPosts(prev => {
          const newSet = new Set(prev);
          if (response.data.approved) {
            newSet.add(postId);
          } else {
            newSet.delete(postId);
          }
          return newSet;
        });
        
        // Update posts list with actual backend response data
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { 
                ...post, 
                approved: response.data.approved,
                approval_notes: response.data.approval_notes,
                approved_at: response.data.approved_at
              } 
            : post
        ));
        
        showSnackbar(
          `Post ${response.data.approved ? 'approved' : 'unapproved'} successfully`, 
          'success'
        );
        
        trackEvent('campaign_post_approval_changed', {
          userId: user?.id,
          campaignId,
          postId,
          approved: response.data.approved
        });
      } else {
        throw new Error(response.error || 'Failed to update approval status');
      }
    } catch (error) {
      console.error('Failed to update post approval:', error);
      
      // ❌ ROLLBACK - Restore previous state on API failure
      setApprovedPosts(previousApprovedPosts);
      setPosts(previousPostsData);
      
      showSnackbar(error.message || 'Failed to update approval status', 'error');
    }
  };

  // Remove post from sequence (soft delete)
  const handleRemovePostFromSequence = (postId, bucketId) => {
    const post = posts.find(p => p.id === postId);
    if (post) {
      // Add to removed posts
      setRemovedPosts(prev => [...prev, { ...post, removedFrom: bucketId, removedAt: new Date() }]);
      
      // Remove from bucket
      setContentBuckets(prev => prev.map(bucket => 
        bucket.id === bucketId 
          ? { ...bucket, posts: bucket.posts.filter(p => p.id !== postId) }
          : bucket
      ));
      
      // Remove approval
      handlePostApproval(postId, false);
      
      showSnackbar('Post removed from sequence (can be restored later)', 'info');
    }
  };

  // Add post to sequence
  const handleAddPostToSequence = (post, bucketId) => {
    setContentBuckets(prev => prev.map(bucket => 
      bucket.id === bucketId 
        ? { ...bucket, posts: [...bucket.posts, { ...post, bucketId }] }
        : bucket
    ));
    
    // Remove from removed posts if it was there
    setRemovedPosts(prev => prev.filter(p => p.id !== post.id));
    
    showSnackbar('Post added to sequence', 'success');
  };

  // Get only approved posts for scheduling with their schedules
  const getApprovedPosts = () => {
    const approvedPostsList = [];
    
    // Get posts from content buckets (which have schedule info)
    contentBuckets.forEach(bucket => {
      bucket.posts.forEach(post => {
        if (approvedPosts.has(post.id)) {
          approvedPostsList.push({
            ...post,
            isManuallyScheduled: postSchedules[post.id]?.isManuallyScheduled || false
          });
        }
      });
    });
    
    return approvedPostsList;
  };
  
  // Handle post schedule edit
  const handlePostScheduleEdit = (postId, newSchedule) => {
    setPostSchedules(prev => ({
      ...prev,
      [postId]: { ...newSchedule, isManuallyScheduled: true }
    }));
    
    // Update the post in content buckets
    setContentBuckets(prev => prev.map(bucket => ({
      ...bucket,
      posts: bucket.posts.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              scheduledDate: newSchedule.date,
              scheduledTime: newSchedule.time,
              isManuallyScheduled: true
            }
          : post
      )
    })));
    
    showSnackbar('Post schedule updated', 'success');
  };

  // Recreate campaign handler
  const handleRecreateCampaign = (feedback) => {
    // This would send feedback to the API to regenerate the campaign
    console.log('Recreating campaign with feedback:', feedback);
    showSnackbar('Campaign recreation initiated...', 'info');
    setRecreateCampaignOpen(false);
    
    trackEvent('campaign_recreate_requested', {
      userId: user?.id,
      campaignId,
      feedback
    });
  };

  // Handle adding new content sequence
  const handleAddSequence = async (sequenceData) => {
    try {
      setLoading(true);
      
      // Call API to create sequence
      const response = await campaignApi.createSequence(campaignId, {
        name: sequenceData.name,
        description: sequenceData.description,
        objective: sequenceData.objective,
        timing: sequenceData.timing,
        frequency: sequenceData.frequency,
        tags: sequenceData.tags || [],
        metadata: {
          startDate: sequenceData.startDate,
          endDate: sequenceData.endDate
        }
      });
      
      if (response.success) {
        // Add the created sequence to local state
        const newSequence = {
          ...response.data,
          posts: [],
          postCount: 0,
          sequence: contentBuckets.length + 1,
          startDate: new Date(sequenceData.startDate),
          endDate: new Date(sequenceData.endDate)
        };
        
        setContentBuckets(prev => [...prev, newSequence]);
        setAddSequenceDialogOpen(false);
        showSnackbar('New content sequence added', 'success');
        
        trackEvent('content_sequence_added', {
          userId: user?.id,
          campaignId,
          sequenceName: sequenceData.name,
          sequenceId: response.data.id
        });
      } else {
        throw new Error(response.error || 'Failed to create sequence');
      }
    } catch (error) {
      console.error('Failed to create sequence:', error);
      showSnackbar(error.message || 'Failed to create sequence', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCampaignUpdate = (updatedCampaign) => {
    setCampaign(updatedCampaign);
    showSnackbar('Campaign updated successfully', 'success');
    
    trackEvent('campaign_updated', {
      userId: user?.id,
      campaignId: updatedCampaign.id,
      changes: ['name'] // Track what was changed
    });
  };

  if (loading) {
    return (
      <StyledContainer maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" height={200} />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" height={60} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
          {[...Array(6)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={200} />
          ))}
        </Box>
      </StyledContainer>
    );
  }

  if (error) {
    return (
      <StyledContainer maxWidth="lg">
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/campaigns/create')}
        >
          Back to Campaign Creator
        </Button>
      </StyledContainer>
    );
  }

  if (!campaign) {
    return (
      <StyledContainer maxWidth="lg">
        <Alert severity="info">
          Campaign not found
        </Alert>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/campaigns')}
          sx={{ mb: 3 }}
        >
          Back to Campaigns
        </Button>
        
        <CampaignHeader
          campaign={campaign}
          totalPosts={posts.length}
          selectedPosts={approvedPosts.size}
          onScheduleSelected={approvedPosts.size > 0 ? handleBulkSchedule : null}
          onCampaignUpdate={handleCampaignUpdate}
          isDemoMode={campaignId === 'demo-campaign-2024'}
        />

        {/* Progress and Expected Outcomes */}
        <ProgressCard sx={{ mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Campaign Progress & Expected Outcomes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Review and approve your content sequences below. Only approved posts will be scheduled.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                              <Chip
                icon={campaign?.campaignGoal && CAMPAIGN_GOAL_CONFIG[campaign.campaignGoal]?.icon ? 
                  React.createElement(CAMPAIGN_GOAL_CONFIG[campaign.campaignGoal].icon) : null}
                label={campaign?.campaignGoal ? CAMPAIGN_GOAL_CONFIG[campaign.campaignGoal]?.label : 'Campaign'}
                color={campaign?.campaignGoal ? (CAMPAIGN_GOAL_CONFIG[campaign.campaignGoal]?.color || 'default') : 'default'}
              />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setRecreateCampaignOpen(true)}
                  startIcon={<AutoAwesomeIcon />}
                >
                  Recreate Campaign
                </Button>
              </Box>
            </Box>
            
            {/* Progress Bar */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {approvedPosts.size} of {posts.length} posts approved for scheduling
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="bold">
                  {Math.round((approvedPosts.size / posts.length) * 100)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(approvedPosts.size / posts.length) * 100}
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    bgcolor: 'success.main'
                  }
                }}
              />
            </Box>

            {/* Expected Outcomes */}
            <Grid container spacing={2}>
              {campaign?.campaignGoal && CAMPAIGN_GOAL_CONFIG[campaign.campaignGoal]?.metrics ? 
              CAMPAIGN_GOAL_CONFIG[campaign.campaignGoal].metrics.map((metric, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <MetricCard>
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
                        {metric.value}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                        {metric.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {metric.description}
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>
              )) : null}
            </Grid>
          </CardContent>
        </ProgressCard>

        {/* Schedule Approved Button */}
        {approvedPosts.size > 0 && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleBulkSchedule}
              startIcon={<ScheduleIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6
                }
              }}
            >
              Schedule {approvedPosts.size} Approved Posts
            </Button>
          </Box>
        )}
      </Box>

      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={selectedTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ bgcolor: 'background.default' }}
          >
            <Tab icon={<FolderIcon />} label="Content Sequences" />
            <Tab icon={<GridIcon />} label="All Posts" />
            <Tab icon={<GroupsIcon />} label="ICP Analysis" />
            <Tab icon={<CollectionsIcon />} label="Assets" />
          </Tabs>
        </Box>

        {/* Content Sequences Tab (moved to first) */}
        <TabPanel value={selectedTab} index={0}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Content Strategy Sequences
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Strategic content sequences that tell your campaign story
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddSequenceIcon />}
                    onClick={() => setAddSequenceDialogOpen(true)}
                    size="small"
                  >
                    Add Sequence
                  </Button>
                  
                  {/* View Mode Toggle */}
                  <ToggleButtonGroup
                    value={sequenceViewMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setSequenceViewMode(newMode)}
                    size="small"
                  >
                  <ToggleButton value="compact">
                    <Tooltip title="Compact View">
                      <CompactIcon />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="expanded">
                    <Tooltip title="Expanded View">
                      <ExpandedIcon />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="timeline">
                    <Tooltip title="Timeline View">
                      <TimelineIcon />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
                </Box>
              </Box>
              
              {/* Quick Actions Bar */}
              <Box sx={{ 
                display: 'flex', 
                gap: 1.5, 
                mt: 2, 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CheckCircleIcon />}
                  onClick={async () => {
                    // Get all post IDs from all buckets
                    const allPostIds = contentBuckets.flatMap(bucket => 
                      bucket.posts.map(post => post.id)
                    );
                    
                    if (allPostIds.length === 0) {
                      showSnackbar('No posts to approve', 'warning');
                      return;
                    }
                    
                    // Store previous state for rollback
                    const previousApprovedPosts = new Set(approvedPosts);
                    const previousApprovedBuckets = new Set(approvedBuckets);
                    const previousContentBuckets = [...contentBuckets];
                    const previousPostsData = [...posts];

                    // ✅ OPTIMISTIC UPDATE - Update UI immediately
                    setApprovedPosts(new Set(allPostIds));
                    setApprovedBuckets(new Set(contentBuckets.map(b => b.id)));
                    setContentBuckets(prev => prev.map(bucket => ({ ...bucket, approved: true })));
                    setPosts(prev => prev.map(post => ({ ...post, approved: true })));

                    try {
                      // Call bulk approval API
                      const response = await campaignApi.bulkApprovePosts(campaignId, allPostIds, true);
                      
                      if (response.success) {
                        // ✅ SUCCESS - Optimistic update was correct
                        showSnackbar(`Approved all ${allPostIds.length} posts`, 'success');
                        
                        trackEvent('campaign_approve_all', {
                          userId: user?.id,
                          campaignId,
                          postsCount: allPostIds.length
                        });
                      } else {
                        throw new Error(response.error || 'Failed to approve all posts');
                      }
                    } catch (error) {
                      console.error('Failed to approve all posts:', error);
                      
                      // ❌ ROLLBACK - Restore previous state on API failure
                      setApprovedPosts(previousApprovedPosts);
                      setApprovedBuckets(previousApprovedBuckets);
                      setContentBuckets(previousContentBuckets);
                      setPosts(previousPostsData);
                      
                      showSnackbar(error.message || 'Failed to approve all posts', 'error');
                    }
                  }}
                  sx={{ minWidth: 'auto' }}
                >
                  Approve All
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setExpandedSequences(new Set(contentBuckets.map(b => b.id)));
                  }}
                >
                  Expand All
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setExpandedSequences(new Set());
                  }}
                >
                  Collapse All
                </Button>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip 
                    label={`${approvedPosts.size}/${posts.length} posts approved`}
                    size="small"
                    color={approvedPosts.size === posts.length ? "success" : "default"}
                  />
                  {removedPosts.length > 0 && (
                    <Chip
                      label={`${removedPosts.length} removed`}
                      size="small"
                      onClick={() => console.log('Show removed posts:', removedPosts)}
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {sequenceViewMode === 'timeline' ? (
              // Timeline View
              <Box sx={{ mt: 3 }}>
                <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
                  <Typography variant="h6" gutterBottom>Campaign Timeline</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Visual overview of your content schedule
                  </Typography>
                  
                  {/* Timeline Grid */}
                  <Box sx={{ overflowX: 'auto', pb: 2 }}>
                    <Box sx={{ minWidth: 800 }}>
                      {/* Timeline Header - Days */}
                      <Box sx={{ 
                        display: 'flex', 
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                        mb: 2,
                        position: 'sticky',
                        top: 0,
                        bgcolor: 'background.paper',
                        zIndex: 1
                      }}>
                        <Box sx={{ width: 150, p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" fontWeight="bold">Sequence</Typography>
                        </Box>
                        {(() => {
                          // Get date range
                          const allDates = [];
                          contentBuckets.forEach(bucket => {
                            bucket.posts.forEach(post => {
                              if (post.scheduledDate) {
                                allDates.push(post.scheduledDate);
                              }
                            });
                          });
                          
                          if (allDates.length === 0) return null;
                          
                          const minDate = new Date(Math.min(...allDates));
                          const maxDate = new Date(Math.max(...allDates));
                          const days = [];
                          
                          for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
                            days.push(new Date(d));
                          }
                          
                          return days.map((date, idx) => (
                            <Box 
                              key={idx} 
                              sx={{ 
                                flex: '0 0 120px', 
                                p: 1, 
                                textAlign: 'center',
                                borderRight: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <Typography variant="caption" fontWeight="bold">
                                {date.toLocaleDateString('en-US', { weekday: 'short' })}
                              </Typography>
                              <Typography variant="body2">
                                {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </Typography>
                            </Box>
                          ));
                        })()}
                      </Box>
                      
                      {/* Timeline Rows - Sequences */}
                      {contentBuckets.map((bucket) => (
                        <Box key={bucket.id} sx={{ 
                          display: 'flex', 
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          minHeight: 80,
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}>
                          <Box sx={{ 
                            width: 150, 
                            p: 2, 
                            borderRight: '1px solid', 
                            borderColor: 'divider',
                            bgcolor: 'grey.50'
                          }}>
                            <Typography variant="caption" fontWeight="bold">
                              {bucket.sequence}. {bucket.name}
                            </Typography>
                          </Box>
                          
                          {/* Posts positioned by date */}
                          <Box sx={{ flex: 1, position: 'relative' }}>
                            {bucket.posts.map((post) => {
                              if (!post.scheduledDate) return null;
                              
                              // Calculate position
                              const allDates = [];
                              contentBuckets.forEach(b => {
                                b.posts.forEach(p => {
                                  if (p.scheduledDate) allDates.push(p.scheduledDate);
                                });
                              });
                              
                              const minDate = new Date(Math.min(...allDates));
                              const dayIndex = Math.floor((post.scheduledDate - minDate) / (1000 * 60 * 60 * 24));
                              
                              return (
                                <Tooltip 
                                  key={post.id}
                                  title={
                                    <Box>
                                      <Typography variant="caption" display="block">
                                        {post.platform} • {post.scheduledTime}
                                      </Typography>
                                      <Typography variant="caption">
                                        {post.content.substring(0, 100)}...
                                      </Typography>
                                    </Box>
                                  }
                                >
                                  <Box
                                    onClick={() => handlePostEdit(post)}
                                    sx={{
                                      position: 'absolute',
                                      left: `${dayIndex * 120 + 10}px`,
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      width: 100,
                                      p: 1,
                                      borderRadius: 1,
                                      bgcolor: approvedPosts.has(post.id) ? 'success.light' : 'primary.light',
                                      border: '1px solid',
                                      borderColor: approvedPosts.has(post.id) ? 'success.main' : 'primary.main',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        boxShadow: 2,
                                        transform: 'translateY(-50%) scale(1.02)'
                                      },
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <PlatformIcon platform={post.platform} size={16} />
                                      <Typography variant="caption" sx={{ 
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1
                                      }}>
                                        {post.scheduledTime}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  
                  {/* Timeline Legend */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: 'primary.light', border: '1px solid', borderColor: 'primary.main', borderRadius: 0.5 }} />
                      <Typography variant="caption">Draft</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: 'success.light', border: '1px solid', borderColor: 'success.main', borderRadius: 0.5 }} />
                      <Typography variant="caption">Approved</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            ) : (
              // Accordion View (Compact & Expanded)
              <Stack spacing={2}>
                {contentBuckets.map((bucket, bucketIndex) => {
                  const bucketApprovedCount = bucket.posts.filter(p => approvedPosts.has(p.id)).length;
                  const isExpanded = expandedSequences.has(bucket.id);
                  
                  return (
                    <SequenceAccordion
                      key={bucket.id}
                      expanded={isExpanded}
                      onChange={() => {
                        const newExpanded = new Set(expandedSequences);
                        if (isExpanded) {
                          newExpanded.delete(bucket.id);
                        } else {
                          newExpanded.add(bucket.id);
                        }
                        setExpandedSequences(newExpanded);
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          '& .MuiAccordionSummary-content': { 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2
                          } 
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                          <Badge badgeContent={bucket.sequence} color="primary">
                            <FolderIcon />
                          </Badge>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" fontWeight="600">
                                {bucket.name}
                              </Typography>
                              {bucket.approved && (
                                <CheckCircleIcon color="success" fontSize="small" />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {bucket.description}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={`${bucketApprovedCount}/${bucket.posts.length}`}
                            color={bucketApprovedCount === bucket.posts.length ? "success" : "default"}
                            sx={{ minWidth: 45 }}
                          />
                          <DateTimeChip>
                            <CalendarIcon />
                            {bucket.timing}
                          </DateTimeChip>
                        </Box>
                      </AccordionSummary>
                      
                      <AccordionDetails sx={{ p: 3 }}>
                        {/* Sequence Meta Info */}
                        <Box sx={{ 
                          mb: 3, 
                          pb: 2, 
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          gap: 2
                        }}>
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Objective:</strong> {bucket.objective}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <DateTimeChip>
                                <CalendarIcon />
                                {bucket.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {' - '}
                                {bucket.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </DateTimeChip>
                              <DateTimeChip>
                                <TimeIcon />
                                {bucket.frequency}
                              </DateTimeChip>
                              {bucket.tags.slice(0, 2).map((tag, idx) => (
                                <Chip key={idx} label={tag} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
                              ))}
                            </Box>
                          </Box>
                          
                          <Button
                            variant={bucket.approved ? "contained" : "outlined"}
                            color={bucket.approved ? "success" : "inherit"}
                            size="small"
                            startIcon={bucket.approved ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBucketApproval(bucket.id, !bucket.approved);
                            }}
                          >
                            {bucket.approved ? 'Approved' : 'Approve All'}
                          </Button>
                        </Box>
                        
                        {/* Posts Grid */}
                        <Stack spacing={sequenceViewMode === 'compact' ? 1 : 2}>
                          {bucket.posts.map((post, postIndex) => {
                            const isApproved = approvedPosts.has(post.id);
                            
                            return sequenceViewMode === 'compact' ? (
                              // Compact Post View
                              <CompactPostCard
                                key={post.id}
                                approved={isApproved}
                                onClick={() => handlePostApproval(post.id, !isApproved)}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                  <Box sx={{ 
                                    mt: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: 28
                                  }}>
                                    {isApproved ? (
                                      <CheckCircleIcon color="success" fontSize="small" />
                                    ) : (
                                      <RadioButtonUncheckedIcon color="action" fontSize="small" />
                                    )}
                                  </Box>
                                  
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <PostMetaRow>
                                      <PlatformIcon platform={post.platform} />
                                      <DateTimeChip>
                                        <CalendarIcon />
                                        {post.scheduledDate?.toLocaleDateString('en-US', { 
                                          month: 'numeric', 
                                          day: 'numeric' 
                                        }) || 'Not set'}
                                      </DateTimeChip>
                                      <DateTimeChip>
                                        <TimeIcon />
                                        {post.scheduledTime || '--:--'}
                                      </DateTimeChip>
                                    </PostMetaRow>
                                    
                                    <Typography 
                                      variant="body2" 
                                      className="post-content"
                                      sx={{ 
                                        mt: 1,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        lineHeight: 1.4
                                      }}
                                    >
                                      {post.content}
                                    </Typography>
                                  </Box>
                                  
                                  <Box className="post-actions" sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton 
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePostEdit(post);
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemovePostFromSequence(post.id, bucket.id);
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </CompactPostCard>
                            ) : (
                              // Expanded Post View  
                              <Paper
                                key={post.id}
                                sx={{
                                  p: 2,
                                  border: '1px solid',
                                  borderColor: isApproved ? 'success.main' : 'divider',
                                  bgcolor: isApproved ? 'success.light' + '10' : 'background.paper',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: 2
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <Box sx={{ flex: 1, mr: 2 }}>
                                    <PostMetaRow>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PlatformIcon platform={post.platform} />
                                        <Typography variant="caption" fontWeight="500">
                                          {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                                        </Typography>
                                      </Box>
                                      {isApproved && (
                                        <Chip 
                                          icon={<CheckCircleIcon />} 
                                          label="Approved" 
                                          color="success" 
                                          size="small"
                                          sx={{ height: 24 }}
                                        />
                                      )}
                                    </PostMetaRow>
                                    
                                    <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                                      {post.content}
                                    </Typography>
                                    
                                    {post.hashtags && post.hashtags.length > 0 && (
                                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                        {post.hashtags.map((tag, idx) => (
                                          <Typography key={idx} variant="caption" color="primary">
                                            #{tag}
                                          </Typography>
                                        ))}
                                      </Box>
                                    )}
                                    
                                    <PostMetaRow>
                                      <DateTimeChip>
                                        <CalendarIcon />
                                        {post.scheduledDate?.toLocaleDateString('en-US', { 
                                          month: 'short', 
                                          day: 'numeric' 
                                        }) || 'Not scheduled'}
                                      </DateTimeChip>
                                      <DateTimeChip>
                                        <TimeIcon />
                                        {post.scheduledTime || 'No time'}
                                      </DateTimeChip>
                                    </PostMetaRow>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                                    <IconButton 
                                      size="small"
                                      color={isApproved ? "success" : "default"}
                                      onClick={() => handlePostApproval(post.id, !isApproved)}
                                    >
                                      {isApproved ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                    </IconButton>
                                    <IconButton 
                                      size="small"
                                      onClick={() => handlePostEdit(post)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton 
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemovePostFromSequence(post.id, bucket.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Paper>
                            );
                          })}
                          
                          {/* Add Post Button */}
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            size="small"
                            sx={{ alignSelf: 'flex-start', mt: 1 }}
                            onClick={() => handleCreatePost(bucket.id)}
                          >
                            Add Post to Sequence
                          </Button>
                        </Stack>
                      </AccordionDetails>
                    </SequenceAccordion>
                  );
                })}
              </Stack>
            )}
          </Box>
        </TabPanel>

        {/* All Posts Tab (moved to second) */}
        <TabPanel value={selectedTab} index={1}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                All Campaign Posts
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Overview of all posts across sequences. You can approve/deny individual posts here.
              </Typography>
            </Box>

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">
                  Campaign Posts ({filteredPosts.length})
                </Typography>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${approvedPosts.size} approved`}
                  color="success"
                  size="small"
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      const allApproved = approvedPosts.size === filteredPosts.length;
                      const postIds = filteredPosts.map(p => p.id);
                      
                      if (postIds.length === 0) {
                        showSnackbar('No posts to process', 'warning');
                        return;
                      }
                      
                      // Call bulk approval API
                      const response = await campaignApi.bulkApprovePosts(campaignId, postIds, !allApproved);
                      
                      if (response.success) {
                        if (allApproved) {
                          // Clear all approvals
                          setApprovedPosts(new Set());
                          setPosts(prev => prev.map(post => 
                            postIds.includes(post.id) ? { ...post, approved: false } : post
                          ));
                          showSnackbar(`Cleared approval for ${postIds.length} posts`, 'success');
                        } else {
                          // Approve all
                          setApprovedPosts(new Set(postIds));
                          setPosts(prev => prev.map(post => 
                            postIds.includes(post.id) ? { ...post, approved: true } : post
                          ));
                          showSnackbar(`Approved ${postIds.length} posts`, 'success');
                        }
                        
                        trackEvent('campaign_bulk_approval_toggle', {
                          userId: user?.id,
                          campaignId,
                          postsCount: postIds.length,
                          approved: !allApproved
                        });
                      } else {
                        throw new Error(response.error || 'Failed to update approvals');
                      }
                    } catch (error) {
                      console.error('Failed to update approvals:', error);
                      showSnackbar(error.message || 'Failed to update approvals', 'error');
                    }
                  }}
                >
                  {approvedPosts.size === filteredPosts.length ? 'Clear All' : 'Approve All'}
                </Button>
                
                <IconButton
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                  size="small"
                >
                  {viewMode === 'grid' ? <ListIcon /> : <GridIcon />}
                </IconButton>
                
                <IconButton onClick={handleMenuClick} size="small">
                  <MoreIcon />
                </IconButton>
              </Box>
            </Box>

            {viewMode === 'grid' ? (
              <CampaignPostGrid
                posts={filteredPosts}
                selectedPosts={Array.from(approvedPosts)}
                onSelectionChange={(postIds) => setApprovedPosts(new Set(postIds))}
                onPostEdit={handlePostEdit}
                onPostSave={handlePostSave}
              />
            ) : (
              <CampaignPostList
                posts={filteredPosts}
                selectedPosts={Array.from(approvedPosts)}
                onSelectionChange={(postIds) => setApprovedPosts(new Set(postIds))}
                onPostEdit={handlePostEdit}
                onPostSave={handlePostSave}
              />
            )}
          </Box>
        </TabPanel>

        {/* ICP Analysis Tab */}
        <TabPanel value={selectedTab} index={2}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Ideal Customer Profile Analysis
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Based on your website and campaign goals, we've identified your ideal customer profile.
                This analysis helps ensure your content resonates with the right audience.
              </Typography>
            </Box>

          {icpAnalysis && (
            <Grid container spacing={3}>
              {/* Demographics */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <PersonIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold">
                        Demographics
                      </Typography>
                    </Box>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Age Range"
                          secondary={icpAnalysis.demographics.ageRange}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Gender"
                          secondary={icpAnalysis.demographics.primaryGender}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Location"
                          secondary={icpAnalysis.demographics.location}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Income Level"
                          secondary={icpAnalysis.demographics.income}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Psychographics */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                        <PsychologyIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold">
                        Psychographics
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Interests
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {icpAnalysis.psychographics.interests.map((interest, idx) => (
                          <Chip key={idx} label={interest} size="small" />
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Pain Points
                      </Typography>
                      {icpAnalysis.psychographics.painPoints.map((pain, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          • {pain}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Behaviors */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                        <TrendingUpIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold">
                        Behaviors
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Active Platforms
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {icpAnalysis.behaviors.socialPlatforms.map((platform, idx) => (
                          <Chip 
                            key={idx} 
                            label={platform} 
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Best Engagement Times
                      </Typography>
                      {icpAnalysis.behaviors.engagementTimes.map((time, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          • {time}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Recommendations */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                        <AutoAwesomeIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold">
                        Content Recommendations
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {icpAnalysis.recommendations.map((rec, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <CheckCircleIcon 
                              sx={{ 
                                color: 'success.main', 
                                mr: 1, 
                                mt: 0.25,
                                fontSize: 20 
                              }} 
                            />
                            <Typography variant="body2">
                              {rec}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          </Box>
        </TabPanel>

        {/* Assets Tab */}
        <TabPanel value={selectedTab} index={3}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Brand Assets & Resources
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Manage your brand colors, fonts, icons, images, and templates
                  </Typography>
                </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<UploadIcon />}
                  variant="outlined"
                  size="small"
                >
                  Upload Asset
                </Button>
                <Button
                  startIcon={<AutoAwesomeIcon />}
                  variant="outlined"
                  size="small"
                  color="secondary"
                >
                  Generate New
                </Button>
              </Box>
            </Box>

            {/* Asset Filters */}
            <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap' }}>
              <Chip 
                label="All" 
                variant={assetFilter === 'all' ? "filled" : "outlined"}
                onClick={() => setAssetFilter('all')}
              />
              <Chip 
                label="Brand" 
                icon={<BrandIcon />}
                variant={assetFilter === 'brand' ? "filled" : "outlined"}
                onClick={() => setAssetFilter('brand')}
              />
              <Chip 
                label="Colors" 
                icon={<PaletteIcon />}
                variant={assetFilter === 'colors' ? "filled" : "outlined"}
                onClick={() => setAssetFilter('colors')}
              />
              <Chip 
                label="Fonts" 
                icon={<FontIcon />}
                variant={assetFilter === 'fonts' ? "filled" : "outlined"}
                onClick={() => setAssetFilter('fonts')}
              />
              <Chip 
                label="Icons" 
                icon={<BrandIcon />}
                variant={assetFilter === 'icons' ? "filled" : "outlined"}
                onClick={() => setAssetFilter('icons')}
              />
              <Chip 
                label="Images" 
                icon={<ImageIcon />}
                variant={assetFilter === 'images' ? "filled" : "outlined"}
                onClick={() => setAssetFilter('images')}
              />
              <Chip 
                label="Templates" 
                icon={<CollectionsIcon />}
                variant={assetFilter === 'templates' ? "filled" : "outlined"}
                onClick={() => setAssetFilter('templates')}
              />
            </Stack>
          </Box>

          {/* Dynamic Asset Display Based on Filter */}
          {(assetFilter === 'all' || assetFilter === 'colors') && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaletteIcon /> Brand Colors
              </Typography>
              <Grid container spacing={2}>
                {brandAssets.colors.map((color) => (
                  <Grid item xs={6} sm={4} md={2} key={color.id}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center', 
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 3 }
                      }}
                    >
                      <Box 
                        sx={{ 
                          width: '100%', 
                          height: 80, 
                          bgcolor: color.value,
                          borderRadius: 1,
                          mb: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }} 
                      />
                      <Typography variant="caption" fontWeight="500">
                        {color.name}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {color.value}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <IconButton size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
                <Grid item xs={6} sm={4} md={2}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <AddIcon sx={{ fontSize: 40, color: 'action.disabled' }} />
                      <Typography variant="caption" color="text.secondary">
                        Add Color
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {(assetFilter === 'all' || assetFilter === 'fonts') && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontIcon /> Brand Fonts
              </Typography>
              <Grid container spacing={2}>
                {brandAssets.fonts.map((font) => (
                  <Grid item xs={12} sm={6} md={4} key={font.id}>
                    <Paper sx={{ p: 3, '&:hover': { boxShadow: 3 } }}>
                      <Typography 
                        variant="h5" 
                        sx={{ fontFamily: font.family, mb: 1 }}
                      >
                        {font.name}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ fontFamily: font.family, mb: 2 }}
                      >
                        The quick brown fox jumps over the lazy dog
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip label={font.type} size="small" />
                        <Box>
                          <IconButton size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {(assetFilter === 'all' || assetFilter === 'icons') && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BrandIcon /> Brand Icons
              </Typography>
              <Grid container spacing={2}>
                {brandAssets.icons.map((icon) => {
                  const IconComponent = icon.component;
                  return (
                    <Grid item xs={6} sm={3} md={2} key={icon.id}>
                      <Paper 
                        sx={{ 
                          p: 2, 
                          textAlign: 'center',
                          cursor: 'pointer',
                          '&:hover': { boxShadow: 3 }
                        }}
                      >
                        <IconComponent sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography variant="caption" display="block">
                          {icon.name}
                        </Typography>
                        <Chip label={icon.type} size="small" sx={{ mt: 1 }} />
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {(assetFilter === 'all' || assetFilter === 'brand' || assetFilter === 'images') && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BrandIcon /> Logos & Images
              </Typography>
              <Grid container spacing={2}>
                {brandAssets.logos.map((logo) => (
                  <Grid item xs={12} sm={6} md={3} key={logo.id}>
                    <AssetCard>
                      <Box
                        sx={{
                          position: 'relative',
                          paddingTop: '75%',
                          bgcolor: 'grey.100',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 2
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                        </Box>
                        
                        {/* Overlay */}
                        <Box
                          className="asset-overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.3s ease'
                          }}
                        >
                          <Stack direction="row" spacing={1}>
                            <IconButton sx={{ color: 'white' }}>
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton sx={{ color: 'white' }}>
                              <DownloadIcon />
                            </IconButton>
                            <IconButton sx={{ color: 'white' }}>
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </Box>
                      </Box>
                      
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {logo.name}
                        </Typography>
                        <Chip label={logo.type} size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </AssetCard>
                  </Grid>
                ))}
                
                {campaignAssets.filter(a => a.type === 'image').map((asset) => (
                  <Grid item xs={12} sm={6} md={3} key={asset.id}>
                    <AssetCard>
                      <Box
                        sx={{
                          position: 'relative',
                          paddingTop: '100%',
                          bgcolor: 'grey.100',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                        </Box>
                        
                        {/* Overlay */}
                        <Box
                          className="asset-overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.3s ease'
                          }}
                        >
                          <Stack direction="row" spacing={1}>
                            <IconButton sx={{ color: 'white' }}>
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton sx={{ color: 'white' }}>
                              <EditIcon />
                            </IconButton>
                            <IconButton sx={{ color: 'white' }}>
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </Box>
                      </Box>
                      
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {asset.name}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {asset.tags.slice(0, 2).map((tag, idx) => (
                            <Chip
                              key={idx}
                              label={tag}
                              size="small"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                          {asset.source === 'generated' && (
                            <Chip
                              icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                              label="AI"
                              size="small"
                              color="secondary"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </AssetCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {(assetFilter === 'templates') && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CollectionsIcon /> Post Templates
              </Typography>
              <Grid container spacing={2}>
                {campaignAssets.filter(a => a.type === 'template').map((template) => (
                  <Grid item xs={12} sm={6} md={4} key={template.id}>
                    <Paper sx={{ p: 3, '&:hover': { boxShadow: 3 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CollectionsIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">
                            {template.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {template.tags.map((tag, idx) => (
                              <Chip key={idx} label={tag} size="small" />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button size="small" startIcon={<VisibilityIcon />}>
                          Preview
                        </Button>
                        <Button size="small" variant="contained">
                          Use Template
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Post Editor Dialog */}
      <Dialog
        open={!!editingPost}
        onClose={() => setEditingPost(null)}
        maxWidth="md"
        fullWidth
      >
        {editingPost && (
          <PostEditor
            post={editingPost}
            onSave={handlePostSave}
            onCancel={() => setEditingPost(null)}
            onContentChange={() => setUnsavedChanges(true)}
          />
        )}
      </Dialog>

      {/* Create Post Dialog */}
      <Dialog
        open={!!creatingPost}
        onClose={() => setCreatingPost(null)}
        maxWidth="md"
        fullWidth
      >
        {creatingPost && (
          <PostEditor
            post={creatingPost}
            onSave={handlePostSave}
            onCancel={() => setCreatingPost(null)}
            onContentChange={() => setUnsavedChanges(true)}
            isCreating={true}
            campaignData={campaign}
            bucketInfo={creatingPost.bucket}
          />
        )}
      </Dialog>

      {/* Schedule Dialog - Updated to use approved posts */}
      <ScheduleDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        onConfirm={handleScheduleConfirm}
        posts={getApprovedPosts()}
        sequences={contentBuckets}
      />

      {/* Recreate Campaign Dialog */}
      <Dialog
        open={recreateCampaignOpen}
        onClose={() => setRecreateCampaignOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recreate Campaign with Feedback
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Provide feedback on what to improve in the regenerated campaign.
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="E.g., More focus on product benefits, less on features. Include more customer testimonials. Make CTAs stronger."
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setRecreateCampaignOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => handleRecreateCampaign('feedback')}
              startIcon={<AutoAwesomeIcon />}
            >
              Recreate Campaign
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={async () => { 
          try {
            const postIds = posts.map(p => p.id);
            const response = await campaignApi.bulkApprovePosts(campaignId, postIds, true);
            
            if (response.success) {
              setApprovedPosts(new Set(postIds));
              setPosts(prev => prev.map(post => ({ ...post, approved: true })));
              showSnackbar(`Approved all ${postIds.length} posts`, 'success');
            } else {
              throw new Error(response.error || 'Failed to approve all posts');
            }
          } catch (error) {
            console.error('Failed to approve all posts:', error);
            showSnackbar(error.message || 'Failed to approve all posts', 'error');
          }
          handleMenuClose(); 
        }}>
          Approve All Posts
        </MenuItem>
        <MenuItem onClick={async () => { 
          try {
            const postIds = posts.map(p => p.id);
            const response = await campaignApi.bulkApprovePosts(campaignId, postIds, false);
            
            if (response.success) {
              setApprovedPosts(new Set());
              setPosts(prev => prev.map(post => ({ ...post, approved: false })));
              showSnackbar(`Cleared approval for all ${postIds.length} posts`, 'success');
            } else {
              throw new Error(response.error || 'Failed to clear approvals');
            }
          } catch (error) {
            console.error('Failed to clear approvals:', error);
            showSnackbar(error.message || 'Failed to clear approvals', 'error');
          }
          handleMenuClose(); 
        }}>
          Clear All Approvals
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          Export Campaign
        </MenuItem>
      </Menu>

      {/* Add Sequence Dialog */}
      <Dialog
        open={addSequenceDialogOpen}
        onClose={() => setAddSequenceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Add New Content Sequence</Typography>
            <IconButton onClick={() => setAddSequenceDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Sequence Name"
              placeholder="e.g., Customer Success Stories"
              sx={{ mb: 2 }}
              id="sequence-name"
            />
            
            <TextField
              fullWidth
              label="Description"
              placeholder="What is the purpose of this sequence?"
              multiline
              rows={2}
              sx={{ mb: 2 }}
              id="sequence-description"
            />
            
            <TextField
              fullWidth
              label="Objective"
              placeholder="e.g., Build trust through social proof"
              sx={{ mb: 2 }}
              id="sequence-objective"
            />
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  id="sequence-start-date"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  id="sequence-end-date"
                />
              </Grid>
            </Grid>
            
            <TextField
              fullWidth
              label="Posting Frequency"
              placeholder="e.g., Every 3 days"
              sx={{ mb: 2 }}
              id="sequence-frequency"
            />
            
            <TextField
              fullWidth
              label="Tags (comma separated)"
              placeholder="e.g., testimonial, social-proof, trust"
              sx={{ mb: 2 }}
              id="sequence-tags"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setAddSequenceDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={() => {
              // Get values from the form
              const name = document.getElementById('sequence-name').value;
              const description = document.getElementById('sequence-description').value;
              const objective = document.getElementById('sequence-objective').value;
              const startDate = document.getElementById('sequence-start-date').value;
              const endDate = document.getElementById('sequence-end-date').value;
              const frequency = document.getElementById('sequence-frequency').value;
              const tags = document.getElementById('sequence-tags').value.split(',').map(t => t.trim()).filter(Boolean);
              
              // Calculate timing based on dates
              const start = new Date(startDate);
              const end = new Date(endDate);
              const weekDiff = Math.ceil((end - start) / (7 * 24 * 60 * 60 * 1000));
              const timing = weekDiff <= 1 ? 'Week ' + contentBuckets.length : `Week ${contentBuckets.length}-${contentBuckets.length + weekDiff}`;
              
              handleAddSequence({
                name,
                description,
                objective,
                startDate,
                endDate,
                frequency,
                timing,
                tags
              });
            }}
          >
            Create Sequence
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </StyledContainer>
  );
};

export default CampaignReviewPage; 